import React, { useMemo, useState, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  ModuleRegistry,
  AllCommunityModule,
} from 'ag-grid-community';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import { confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';

import CommentIcon from '../../assets/icons/comment.svg';
import GridHeader from '../../components/GridHeader/GridHeader';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import ExcelCommentBox, { ExcelComment } from '../../components/ExcelComment/ExcelCommentBox';
import { setUnsavedChanges } from '../../utils/unsavedChanges';
import './AGGrid.scss';

import apiAxios, { apiWithRetry } from '../../utils/api';

import SuspenseLoader from '../../components/SuspenseLoader/SuspenseLoader';

ModuleRegistry.registerModules([AllCommunityModule]);

const months = [
  'title',
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  'Total'
];
const quarters = ['title', 'Q1', 'Q2', 'Q3', 'Q4', 'Total'];

const monthMap: Record<string, number> = {
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  May: 5,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12
};

const cleanCurrency = (val: any) => Number(String(val).replace(/[$,%]/g, '').replace(/,/g, '')) || 0;

interface AGGridProps {
  filterResult: {
    year: number | null;
    customer: number | null;
    category: number | null;
    brand: number | null;
    isSummaryView: boolean;
    customerName: string | null,
    categoryName: string | null,
    brandName: string | null
  } | null;
}

const AGGrid: React.FC<AGGridProps> = ({ filterResult }) => {
  const gridRef = useRef<any>(null);

  const [isQuaterly, setIsQuaterly] = useState(false);
  const [comments, setComments] = useState<Record<string, ExcelComment>>({});
  const [activeCell, setActiveCell] = useState<any>(null);
  const [commentPosition, setCommentPosition] = useState<any>(null);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [menuPosition, setMenuPosition] = useState<any>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [gridData, setGridData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [hoveredCell, setHoveredCell] = useState<any>(null);
  const [hoverPosition, setHoverPosition] = useState<any>(null);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const hoverTimeoutRef = useRef<any>(null);
  const [pendingInputs, setPendingInputs] = useState<
    Record<string, { value?: number; comment?: string }>
  >({});
  const [apiCommentsData, setApiCommentsData] = useState<Record<string, ExcelComment>>({});
  const hasChanges = Object.keys(pendingInputs).length > 0;
  const activeColumns = isQuaterly ? quarters : months;
  const toast = useRef<Toast>(null);
  const currentUser = localStorage.getItem('username');
  const initials = currentUser
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase();

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const fetchGridData = async () => {
    try {
      setLoading(true);

      const payload = {
        ...filterResult,
        viewType: isQuaterly ? 'Q' : 'M'
      };

      const res = await apiWithRetry(() => apiAxios.get('/fec/', { params: payload }));
      if (res?.data?.success) {
        setGridData(res.data.data);
        setPendingInputs({});
        setUnsavedChanges(false);
      }
    } catch (err) {
      console.error('Grid API error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!filterResult) return;

    const fetchComments = async () => {
      try {
        const params = {
          year: filterResult.year,
          customerId: filterResult.customer,
          categoryId: filterResult.category,
          brandId: filterResult.brand
        };

        const res = await apiWithRetry(() => apiAxios.get('/fec/comment', { params }));
        const rawComments = res?.data?.data?.comments || [];

        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const mapped = mapCommentsToGrid(rawComments);
        setApiCommentsData(mapped);
      } catch (err) {
        console.error('Comment API error:', err);
      }
    };

    fetchComments();
  }, [filterResult]);

  useEffect(() => {
    if (!filterResult) return;
    fetchGridData();
  }, [filterResult, isQuaterly]);

  useEffect(() => {
    if (!showMenu) return;

    const handleOutsideClick = () => {
      setShowMenu(false);
    };

    document.addEventListener('pointerdown', handleOutsideClick);

    return () => {
      document.removeEventListener('pointerdown', handleOutsideClick);
    };
  }, [showMenu]);

  useEffect(() => {
    const hasUnsavedChanges = Object.keys(pendingInputs).length > 0;
    setUnsavedChanges(hasUnsavedChanges);
  }, [pendingInputs]);

  /* ---------------- BUILD GRID DATA ---------------- */

  const createHeaderRow = (id: string) => {
    const row: any = {
      rowId: id,
      section: 'header'
    };

    const headerTitle = filterResult?.brandName
      || filterResult?.categoryName
      || filterResult?.customerName
      || 'Title';

    activeColumns.forEach(col => {
      row[col] = col === 'title' ? headerTitle : col;
    });

    return row;
  };

  const handleRefresh = () => {
    confirmDialog({
      message: 'Are you sure you want to refresh? All unsaved changes will be lost.',
      header: 'Confirm Refresh',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        setPendingInputs({});
        setUnsavedChanges(false);
        fetchGridData(); // 🔥 re-fetch API
      },
      reject: () => { },
      acceptLabel: 'Yes',
      rejectLabel: 'No'
    });
  };

  const isNumeric = (val: any) => {
    if (val === null || val === undefined || val === '') return false;

    if (typeof val === 'number') return !Number.isNaN(val);

    return /^-?\d+(\.\d+)?$/.test(String(val).replace(/,/g, ''));
  };

  const mapCommentsToGrid = (apiComments: any[]): Record<string, ExcelComment> => {
    const mapped: Record<string, ExcelComment> = {};

    apiComments.forEach((c) => {
      const monthLabel = Object.keys(monthMap).find(
        key => monthMap[key] === c.month
      );

      if (!monthLabel) return;

      const cellId = `${c.driverCode}-${monthLabel}`;

      mapped[cellId] = {
        cellId,
        text: c.comment,
        user: String(c.updatedBy), // or map user name if needed
        timestamp: new Date(c.updatedAt).getTime()
      };
    });

    return mapped;
  };

  const { rows: rowData, extractedComments } = useMemo(() => {
    if (!gridData || typeof gridData !== 'object') {
      return { rows: [], extractedComments: {} };
    }

    const rows: any[] = [];
    const apiComments: Record<string, ExcelComment> = {};
    let idCounter = 0;

    const createRowId = () => `row-${idCounter++}`;

    const sectionKeys = Object.keys(gridData);

    Object.entries(gridData).forEach(([sectionKey, sectionRows]: any, sectionIndex) => {
      if (!Array.isArray(sectionRows)) return;

      /* ---------------- HEADER FOR SECTION ---------------- */

      const totalSections = sectionKeys.length;

      if (sectionIndex >= totalSections - 3) {
        rows.push(createHeaderRow(createRowId()));
      }

      sectionRows.forEach((r: any, rowIndex: number) => {
        if (sectionIndex === 4 && (rowIndex === 2)) {
          rows.push(createHeaderRow(createRowId()));
        }

        const editableMap: Record<string, boolean> = {};
        const values: Record<string, any> = {};

        r.periods.forEach((p: any) => {
          editableMap[p.label] = p.isEditable === true;
          values[p.label] = cleanCurrency(p.value);
        });

        const cleanTitle = (title: string) => {
          if (!title) return title;

          return title
            .replace(/\s*–\s*Total/i, '')
            .replace(/\s*-\s*Total/i, '')
            .trim();
        };
        const isTotalRow = r.title?.toLowerCase().includes('total');

        const isFirstSection = sectionIndex === 0;
        const isSecondSection = sectionIndex === 1;

        const isNoTotal = (sectionIndex === 4 && [0, 1].includes(rowIndex))
          || (sectionIndex === 2 && rowIndex === 9);

        rows.push({
          rowId: createRowId(),
          title: cleanTitle(r.title),
          isTotalRow,
          driverId: r.driverId || r.id,
          ...values,
          sectionIndex,
          rowIndex,
          isNoTotal,
          Total: cleanCurrency(r.total),
          editableMap,
          section: sectionKey,
          isBurnImpact: (sectionIndex === 3 && rowIndex === 3) || (sectionIndex === 2 && rowIndex === 8),
          isPlannedArrival: sectionIndex === 4 && rowIndex === 0,
          isPreYear: isSecondSection && (rowIndex === 1 || rowIndex === 2),
          isPastMonthRowForNewForcast: sectionIndex === 4 && (rowIndex === 2 || rowIndex === 7),
          isVarianceRow: sectionIndex === 4 && (rowIndex === 4 || rowIndex === 9 || rowIndex === 10 || rowIndex === 13)
        });

        /* ---------------- SPECIAL SPACING RULES ---------------- */

        if (sectionIndex === 4) {
          // third section spacing pattern
          if (rowIndex === 1) rows.push({ rowId: createRowId(), section: 'spacer' });
          if (rowIndex === 4) rows.push({ rowId: createRowId(), section: 'spacer' });
          if (rowIndex === 6) rows.push({ rowId: createRowId(), section: 'spacer' });
          if (rowIndex === 9) rows.push({ rowId: createRowId(), section: 'spacer' });
          if (rowIndex === 11) rows.push({ rowId: createRowId(), section: 'spacer' });
        }
      });

      /* ---------------- SECTION SPACER ---------------- */

      if (sectionIndex !== 0) {
        rows.push({
          rowId: createRowId(),
          title: '',
          section: 'spacer'
        });
      }
    });

    return { rows, extractedComments: apiComments };
  }, [gridData, activeColumns]);

  useEffect(() => {
    if (!apiCommentsData) return;

    setComments(apiCommentsData);
  }, [apiCommentsData]);

  const sanitize = (val: any) => {
    if (!val) return null;

    return String(val)
      .replace(/\s+/g, '_')
      .replace(/[^\w]/g, '')
      .toLowerCase();
  };

  const buildFileName = () => {
    if (!filterResult) return 'FEC_Data.xlsx';

    const parts = [];

    // use name if available else fallback to id
    if (filterResult.brandName || filterResult.brand) {
      parts.push(sanitize(filterResult.brandName ?? `brand_${filterResult.brand}`));
    }

    if (filterResult.categoryName || filterResult.category) {
      parts.push(sanitize(filterResult.categoryName ?? `category_${filterResult.category}`));
    }

    if (filterResult.customerName || filterResult.customer) {
      parts.push(sanitize(filterResult.customerName ?? `customer_${filterResult.customer}`));
    }

    if (filterResult.year) {
      parts.push(String(filterResult.year));
    }

    const name = parts.join('_');

    return `${name}.xlsx`;
  };

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('FEC');

    /* ---------------- HEADER ---------------- */
    const headerTitle = filterResult?.brandName
      || filterResult?.categoryName
      || filterResult?.customerName
      || 'Title';

    const headerColumns = activeColumns.map((col, index) => (index === 0 ? headerTitle : col));

    const headerRow = sheet.addRow(headerColumns);

    headerRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true };
      cell.alignment = {
        vertical: 'middle',
        horizontal: colNumber === 1 ? 'left' : 'center'
      };
    });

    /* ---------------- ROWS ---------------- */
    gridRef.current.api.forEachNode((node: any) => {
      const row = node.data;
      if (!row) return;

      // Preserve spacer rows
      if (row.section === 'spacer') {
        sheet.addRow([]);
        return;
      }

      const rowValues = activeColumns.map((col) => {
        const value = row[col];

        const rowIndex = row?.rowIndex;
        const section = row?.sectionIndex;
        const noTotal = row?.isNoTotal;

        const isPercentageRow = (section === 1 && [4, 5].includes(rowIndex))
          || (section === 2 && rowIndex === 9)
          || (section === 4 && rowIndex === 11);

        // Don't append % to title column
        if (
          isPercentageRow
          && col !== 'title'
          && (col !== 'Total' || (section === 1 && [4, 5].includes(rowIndex)))
          && value !== null
          && value !== undefined
          && value !== ''
        ) {
          return `${value}%`;
        }

        if (col === 'Total' && row.isNoTotal) {
          return 'N/A';
        }

        return value ?? '';
      });

      const excelRow = sheet.addRow(rowValues);

      activeColumns.forEach((col, index) => {
        const cell = excelRow.getCell(index + 1);

        /* ---------------- ALIGNMENT ---------------- */
        cell.alignment = {
          vertical: 'middle',
          horizontal: index === 0 ? 'left' : 'center'
        };

        /* ---------------- HEADER ROW (SECTION HEADER) ---------------- */
        if (row.section === 'header') {
          cell.font = { bold: true };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFEFEFEF' }
          };
        }

        /* ---------------- PLANNED ARRIVAL (RED ROW) ---------------- */
        if (row.isPlannedArrival) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFCCCC' }
          };
          cell.font = { bold: true };
        }

        /* ---------------- PAST MONTH (BLUE CELLS) ---------------- */
        if (row.isPastMonthRowForNewForcast && monthMap[col]) {
          const selectedYear = filterResult?.year;
          const now = new Date();
          const currentYear = now.getFullYear();
          const currentMonth = now.getMonth() + 1;

          if (
            (selectedYear && selectedYear < currentYear)
            || (selectedYear === currentYear && monthMap[col] < currentMonth)
          ) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFCCE5FF' }
            };
          }
        }

        /* ---------------- CUSTOMER DRIVER (GREEN) ---------------- */
        if (row.title?.includes('Consumption Drivers')) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF5E9A7A' } // rgba(94,154,122,0.56)
          };
          cell.font = { bold: true };
        }

        /* ---------------- INVENTORY DRIVER (PURPLE) ---------------- */
        if (row.title?.includes('Inventory Drivers')) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFC1A4FF' } // rgba(193,164,255,0.4)
          };
          cell.font = { bold: true };
        }

        /* ---------------- VARIANCE COLORS ---------------- */
        if (row.isVarianceRow && monthMap[col]) {
          const value = Number(row[col]);

          if (value > 0) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFD4EDDA' } // green
            };
          } else if (value < 0) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF8D7DA' } // red
            };
          }
        }

        /* ---------------- CUSTOM FIRST CELL ---------------- */
        if (row.isFirstCellHighlight && col === 'title') {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF1DC1D2' } // converted from #1DC1D23D (alpha not supported well)
          };
          cell.font = { bold: true };
        }

        /* ---------------- COMMENTS ---------------- */
        const cellId = `${row.driverId}-${col}`;
        const comment = comments[cellId];

        if (comment?.text) {
          cell.note = comment.text;
        }
      });
    });

    /* ---------------- COLUMN WIDTH ---------------- */
    sheet.columns = activeColumns.map((col, index) => ({
      width: index === 0 ? 28 : 14
    }));

    /* ---------------- EXPORT ---------------- */
    const buffer = await workbook.xlsx.writeBuffer();
    const fileName = buildFileName();
    saveAs(new Blob([buffer]), fileName);
  };

  /* ---------------- TOTAL RECALCULATION ---------------- */

  const recalcTotals = (api: any) => {
    const rows: any[] = [];
    api.forEachNode((n: any) => rows.push(n.data));

    const updates: any[] = [];

    rows.forEach((row, idx) => {
      if (!row.isTotalRow) return;

      const totals = Array(activeColumns.length - 2).fill(0);

      for (let i = idx + 1; i < rows.length; i++) {
        const child = rows[i];

        if (
          !child
          || child.section === 'spacer'
          || child.section === 'header'
          || child.isTotalRow
        ) {
          break;
        }

        activeColumns.slice(1, -1).forEach((m, j) => {
          const val = child[m];

          if (child.isNoTotal) return;
          if (isNumeric(val)) {
            totals[j] += Number(val);
          }
        });
      }

      updates.push({
        ...row,
        ...Object.fromEntries(
          activeColumns.slice(1, -1).map((m, j) => [m, totals[j]])
        ),
        Total: totals.reduce((a, b) => a + b, 0)
      });
    });

    // ✅ APPLY ALL AT ONCE (critical fix)
    if (updates.length > 0) {
      api.applyTransaction({ update: updates });
    }
  };

  const onCellValueChanged = (e: any) => {
    const month = e.colDef.field;

    if (!month || month === 'title' || month === 'Total') return;

    const updatedRow = e.data;

    // calculate row total (Jan → Dec)
    const rowTotal = activeColumns
      .slice(1, -1)
      .reduce((sum, m) => {
        const val = updatedRow[m];
        return sum + (isNumeric(val) ? Number(val) : 0);
      }, 0);

    if (!updatedRow.isNoTotal) {
      updatedRow.Total = rowTotal;
    } else {
      updatedRow.Total = null;
    }

    e.api.refreshCells({
      rowNodes: [e.node],
      columns: ['Total'],
      force: true
    });

    const sourceDriver = updatedRow.driverId;
    const { newValue } = e;
    let found = false;

    if (sourceDriver === 'PRELIM_ACTUALS') {
      const selectedYear = filterResult?.year;
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      const editedMonthNumber = monthMap[month];

      if (
        selectedYear !== currentYear
        || editedMonthNumber !== currentMonth
      ) {
        return;
      }

      e.api.forEachNode((node: any) => {
        if (found) return;

        if (node.data?.driverId === 'GSBR_CY_NEW_FORECAST') {
          found = true;

          const updatedTarget = {
            ...node.data,
            [month]: Number(newValue),
          };

          const targetTotal = activeColumns
            .slice(1, -1)
            .reduce((sum, m) => {
              const val = updatedTarget[m];
              return sum + (isNumeric(val) ? Number(val) : 0);
            }, 0);

          updatedTarget.Total = targetTotal;

          e.api.applyTransaction({
            update: [updatedTarget]
          });

          const targetCellId = `GSBR_CY_NEW_FORECAST-${month}`;

          setPendingInputs((prev) => ({
            ...prev,
            [targetCellId]: {
              ...prev[targetCellId],
              value: Number(newValue)
            }
          }));
        }
      });
    }

    if (sourceDriver === 'GSBR_SUPPLY_RISK') {
      const { api } = e;

      let targetNode: any = null;

      api.forEachNode((node: any) => {
        if (node.data?.driverId === 'GSBR_CY_NEW_CONSTRAINED') {
          targetNode = node;
        }
      });

      if (!targetNode) return;

      const selectedYear = filterResult?.year;
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      const editedMonthNumber = monthMap[month];

      if (
        (selectedYear && selectedYear < currentYear)
        || (selectedYear === currentYear && editedMonthNumber < currentMonth)
      ) {
        return;
      }

      const supplyRiskValue = Number(e.newValue) || 0;
      const previousSupplyRiskValue = Number(e.oldValue) || 0;
      const difference = supplyRiskValue - previousSupplyRiskValue;

      const existingValue = Number(targetNode.data[month]) || 0;

      const calculatedValue = Math.round(existingValue + difference);

      const updatedTarget = {
        ...targetNode.data,
        [month]: calculatedValue
      };
      updatedTarget.Total = activeColumns
        .slice(1, -1)
        .reduce((acc, m) => {
          const v = updatedTarget[m];
          return acc + (isNumeric(v) ? Number(v) : 0);
        }, 0);

      api.applyTransaction({
        update: [updatedTarget]
      });

      const targetCellId = `GSBR_CY_NEW_CONSTRAINED-${month}`;

      setPendingInputs((prev) => ({
        ...prev,
        [targetCellId]: {
          ...prev[targetCellId],
          value: calculatedValue
        }
      }));
    }

    if (sourceDriver === 'GSBR_PRIOR_FCST_SUPPLY_RISK') {
      const { api } = e;

      let targetNode: any = null;

      api.forEachNode((node: any) => {
        if (node.data?.driverId === 'GSBR_CY_PRIOR_FORECAST') {
          targetNode = node;
        }
      });

      if (!targetNode) return;

      const supplyRiskValue = Number(e.newValue) || 0;
      const previousSupplyRiskValue = Number(e.oldValue) || 0;
      const difference = supplyRiskValue - previousSupplyRiskValue;

      const existingForecastValue = Number(targetNode.data[month]) || 0;

      const calculatedValue = Math.round(
        existingForecastValue + difference
      );

      const updatedTarget = {
        ...targetNode.data,
        [month]: calculatedValue
      };

      updatedTarget.Total = activeColumns
        .slice(1, -1)
        .reduce((acc, m) => {
          const v = updatedTarget[m];
          return acc + (isNumeric(v) ? Number(v) : 0);
        }, 0);

      api.applyTransaction({
        update: [updatedTarget]
      });

      const targetCellId = `GSBR_CY_PRIOR_FORECAST-${month}`;

      setPendingInputs((prev) => ({
        ...prev,
        [targetCellId]: {
          ...prev[targetCellId],
          value: calculatedValue
        }
      }));
    }

    if (sourceDriver === 'GSBR_DP_FORECAST') {
      const { api } = e;

      let targetNode: any = null;
      let custOrderNode: any = null;

      api.forEachNode((node: any) => {
        if (node.data?.driverId === 'GSBR_VAR_TO_DP') {
          targetNode = node;
        }

        if (node.data?.driverId === 'GSBR_CY_NEW_FORECAST') {
          custOrderNode = node;
        }
      });

      if (!targetNode || !custOrderNode) return;

      const dpForecastValue = Number(e.newValue) || 0;
      const custOrderValue = Number(custOrderNode.data[month]) || 0;

      const calculatedValue = Math.round(custOrderValue - dpForecastValue);

      const updatedTarget = {
        ...targetNode.data,
        [month]: calculatedValue
      };

      updatedTarget.Total = activeColumns
        .slice(1, -1)
        .reduce((acc, m) => {
          const v = updatedTarget[m];
          return acc + (isNumeric(v) ? Number(v) : 0);
        }, 0);
      api.applyTransaction({
        update: [updatedTarget]
      });

      const targetCellId = `GSBR_VAR_TO_DP-${month}`;

      setPendingInputs((prev) => ({
        ...prev,
        [targetCellId]: {
          ...prev[targetCellId],
          value: calculatedValue
        }
      }));
    }

    if (sourceDriver === 'INV_DISPLAYS_ABS_VOL') {
      const { api } = e;
      let foundVol = false;

      api.forEachNode((node: any) => {
        if (foundVol) return;

        if (node.data?.driverId === 'INV_DISPLAYS_BURN_IMPACT') {
          foundVol = true;

          const monthIndex = activeColumns.indexOf(month);
          const prevMonth = monthIndex > 1 ? activeColumns[monthIndex - 1] : null;
          const prevValue = prevMonth
            ? Number(e.data[prevMonth]) || 0
            : 0;

          const currentValue = Number(e.newValue) || 0;

          const calculatedValue = Math.round(
            (prevValue + currentValue) * -0.45
          );

          const updatedTarget = {
            ...node.data,
            [month]: calculatedValue
          };

          updatedTarget.Total = activeColumns
            .slice(1, -1)
            .reduce((acc, m) => {
              const v = updatedTarget[m];
              return acc + (isNumeric(v) ? Number(v) : 0);
            }, 0);

          api.applyTransaction({
            update: [updatedTarget]
          });

          const targetCellId = `INV_DISPLAYS_BURN_IMPACT-${month}`;

          setPendingInputs((prev) => ({
            ...prev,
            [targetCellId]: {
              ...prev[targetCellId],
              value: calculatedValue
            }
          }));
        }
      });
    }

    if (sourceDriver === 'CONS_BASE_TRENDS_PCT') {
      const { api } = e;
      let targetNode: any = null;
      let pyActualNode: any = null;

      api.forEachNode((node: any) => {
        if (node.data?.driverId === 'CONS_BASE_TRENDS_AMT') {
          targetNode = node;
        }

        if (node.data?.driverId === 'FEC_PY_ACTUAL') {
          pyActualNode = node;
        }
      });

      if (!targetNode || !pyActualNode) return;
      const percentValue = Number(e.newValue) || 0;

      const pyValue = Number(pyActualNode.data[month]) || 0;

      const calculatedValue = Math.round(pyValue * (percentValue / 100));
      const updatedTarget = {
        ...targetNode.data,
        [month]: calculatedValue
      };

      // 👉 recalc total
      updatedTarget.Total = activeColumns
        .slice(1, -1)
        .reduce((acc, m) => {
          const v = updatedTarget[m];
          return acc + (isNumeric(v) ? Number(v) : 0);
        }, 0);

      // 👉 apply update
      api.applyTransaction({
        update: [updatedTarget]
      });

      // 👉 also track pending inputs (IMPORTANT)
      const targetCellId = `CONS_BASE_TRENDS_AMT-${month}`;

      setPendingInputs((prev) => ({
        ...prev,
        [targetCellId]: {
          ...prev[targetCellId],
          value: calculatedValue
        }
      }));
    }

    // recalc section totals after row total changes
    recalcTotals(e.api);

    if (updatedRow.section === 'CONS_TOTAL') {
      console.log('entry');
      const { api } = e;

      let headerNode: any = null;
      let pyNode: any = null;
      let forecastNode: any = null;

      api.forEachNode((node: any) => {
        if (node.data?.section === 'header' && node.data?.title?.includes('Consumption')) {
          headerNode = node;
        }

        if (node.data?.driverId === 'FEC_PY_ACTUAL') {
          pyNode = node;
        }

        if (node.data?.driverId === 'FEC_CY_FORECAST') {
          forecastNode = node;
        }
      });

      if (!headerNode || !pyNode || !forecastNode) return;

      const headerValue = Number(headerNode.data[month]) || 0;
      const pyValue = Number(pyNode.data[month]) || 0;

      const calculatedValue = Math.round(pyValue + headerValue);

      const updatedForecast = {
        ...forecastNode.data,
        [month]: calculatedValue
      };

      updatedForecast.Total = activeColumns
        .slice(1, -1)
        .reduce((acc, m) => {
          const v = updatedForecast[m];
          return acc + (isNumeric(v) ? Number(v) : 0);
        }, 0);

      api.applyTransaction({
        update: [updatedForecast]
      });

      // ✅ track pending inputs
      const targetCellId = `FEC_CY_FORECAST-${month}`;

      setPendingInputs((prev) => ({
        ...prev,
        [targetCellId]: {
          ...prev[targetCellId],
          value: calculatedValue
        }
      }));
    }

    const cellId = `${e.data.driverId}-${month}`;

    setPendingInputs((prev) => ({
      ...prev,
      [cellId]: {
        ...prev[cellId],
        value: e.newValue
      }
    }));

    if (sourceDriver === 'PRELIM_ACTUALS') {
      const targetCellId = `GSBR_CY_NEW_FORECAST-${month}`;

      setPendingInputs((prev) => ({
        ...prev,
        [targetCellId]: {
          ...prev[targetCellId],
          value: newValue
        }
      }));
    }
  };

  /* ---------------- COLUMN DEFINITIONS ---------------- */

  const columnDefs: ColDef[] = useMemo(
    () => {
      const headerTitle = filterResult?.brandName
        || filterResult?.categoryName
        || filterResult?.customerName
        || 'Title';

      return activeColumns.map((m, index) => ({
        field: m,
        headerName: index === 0 ? headerTitle : m,
        flex: index === 0 ? 2 : 1,
        minWidth: index === 0 ? 185 : 101,
        maxWidth: index === 0 ? undefined : 185,
        sortable: false,

        cellStyle: index === 0
          ? { textAlign: 'left' }
          : { textAlign: 'center' },

        headerClass: index === 0
          ? 'excel-header-left'
          : 'excel-header-center',

        valueSetter: (params) => {
          const { newValue } = params;

          if (newValue === '' || newValue === null || newValue === undefined) {
            params.data[params.colDef.field!] = 0;
            return true;
          }
          const numericRegex = /^-?\d+(\.\d+)?$/;

          if (!numericRegex.test(String(newValue).replace(/,/g, ''))) {
            toast.current?.show({
              severity: 'error',
              summary: 'Invalid Input',
              detail: 'Please enter only numeric digits.',
              life: 3000
            });

            return false;
          }

          params.data[params.colDef.field!] = Number(newValue);
          return true;
        },

        editable: params => {
          if (isQuaterly) return false;
          const { field } = params.colDef;

          if (
            field === 'title'
            || field === 'Total'
            || params.data.section === 'spacer'
            || params.data.section === 'header'
            || params.data.driverId === 'CONS_BASE_TRENDS_AMT'
          ) return false;

          return field ? params.data?.editableMap?.[field] === true : false;
        },

        valueParser: params => {
          return params.newValue;
        },

        valueFormatter: params => {
          if (params.data?.section === 'header') return params.value;

          if (params.column.getColId() === 'title') return params.value;
          if (params.value == null || params.value === '') return '';

          const value = Number(params.value);
          const rowIndex = params.data?.rowIndex;
          const section = params.data?.sectionIndex;
          const noTotal = params.data?.isNoTotal;

          const isPercentageRow = (section === 1 && [4, 5].includes(rowIndex))
            || (section === 2 && rowIndex === 9)
            || (section === 4 && rowIndex === 11);

          if (
            isPercentageRow
            && (
              params.column.getColId() !== 'Total'
              || (section === 1 && [4, 5].includes(rowIndex))
            )
          ) {
            return `${value}%`;
          }

          if (params.column.getColId() === 'Total' && params.data?.isNoTotal) {
            return 'N/A';
          }

          return new Intl.NumberFormat('en-US').format(params.value);
        },

        cellClass: p => {
          const title = p.data?.title;
          const col = p.column.getColId();

          if (title?.includes('Consumption Drivers')) {
            return 'excel-consumption-header';
          }

          if (title?.includes('Inventory Drivers')) {
            return 'excel-inventory-header';
          }

          if (p.data?.section === 'header') {
            return 'excel-section-header';
          }

          if (p.data?.isPlannedArrival) {
            return 'excel-planned-arrival-row';
          }

          if (p.data?.isBurnImpact) {
            return 'excel-burn-impact';
          }

          if (p.data?.isPreYear) {
            return 'excel-pre-year';
          }

          if (p.data?.isPastMonthRowForNewForcast && monthMap[col] !== undefined) {
            const selectedYear = filterResult?.year;
            const now = new Date();
            const currentYear = now.getFullYear();

            const currentMonth = now.getMonth() + 1; // convert to 1-based

            if (selectedYear && selectedYear < currentYear) {
              return 'excel-past-month';
            }

            if (selectedYear === currentYear) {
              if (monthMap[col] < currentMonth) {
                return 'excel-past-month';
              }
            }
          }

          if (p.data?.isVarianceRow && monthMap[col] !== undefined) {
            const value = Number(p.value);

            if (value > 0) return 'excel-positive-cell';
            if (value < 0) return 'excel-negative-cell';
          }

          return p?.node?.rowIndex && p.node.rowIndex % 2
            ? 'excel-row-alt'
            : 'excel-row-light';
        },

        cellRenderer: (p: any) => {
          const cellId = `${p.data.driverId}-${p.column.getColId()}`;
          const hasComment = comments[cellId];

          return (
            <div className="cell-wrap">
              {p.valueFormatted ?? p.value}

              {hasComment && !isQuaterly && (
                <img
                  src={CommentIcon}
                  alt="comment"
                  className="excel-comment-icon"
                  onMouseEnter={(e) => {
                    clearTimeout(hoverTimeoutRef.current);

                    const rect = (e.target as HTMLElement).getBoundingClientRect();

                    setHoverPosition({
                      top: rect.bottom + 6,
                      left: rect.left
                    });

                    setHoveredCell({
                      driverId: p.data.driverId,
                      col: p.column.getColId()
                    });
                  }}
                  onMouseLeave={() => {
                    hoverTimeoutRef.current = setTimeout(() => {
                      setHoveredCell(null);
                    }, 200);
                  }}
                  onClick={e => {
                    e.stopPropagation();

                    const rect = (e.target as HTMLElement).getBoundingClientRect();

                    setCommentPosition({
                      top: rect.bottom + 8,
                      left: rect.left
                    });

                    setShowCommentBox(true);
                    setActiveCell({
                      driverId: p.data.driverId,
                      col: p.column.getColId(),
                      readOnly: !!comments[cellId]
                    });
                  }}
                />
              )}
            </div>
          );
        }
      }));
    },
    [activeColumns, isQuaterly, comments]
  );

  /* ---------------- COMMENT SAVE ---------------- */

  const handleSaveComment = (comment: ExcelComment) => {
    setComments(prev => ({
      ...prev,
      [comment.cellId]: comment
    }));

    setPendingInputs(prev => ({
      ...prev,
      [comment.cellId]: {
        ...prev[comment.cellId],
        comment: comment.text
      }
    }));

    gridRef.current?.api?.refreshCells({ force: true });

    setShowCommentBox(false);
  };

  const buildPayload = () => {
    if (!filterResult) return null;

    const api = gridRef.current?.api;
    if (!api) return null;

    const monthsPayload: Record<number, any[]> = {};

    Object.entries(pendingInputs).forEach(([cellId, data]) => {
      const [driverId, monthLabel] = cellId.split('-');

      const monthNum = monthMap[monthLabel];
      if (!monthNum) return;

      if (!monthsPayload[monthNum]) {
        monthsPayload[monthNum] = [];
      }

      const payloadItem: any = {
        driverCode: driverId
      };

      if (data.value !== undefined) {
        payloadItem.value = data.value;
      }

      if (data.comment !== undefined) {
        payloadItem.comment = data.comment;
      }

      monthsPayload[monthNum].push(payloadItem);
    });

    return {
      year: filterResult.year,
      customerId: filterResult.customer,
      categoryId: filterResult.category,
      brandId: filterResult.brand,
      months: Object.entries(monthsPayload).map(([month, inputs]) => ({
        month: Number(month),
        inputs
      }))
    };
  };

  const handleSaveChanges = async () => {
    const payload = buildPayload();
    if (!payload) return;

    try {
      setLoading(true);

      const response = await apiWithRetry(() => apiAxios.post('/fec/save-manual-input', payload));

      if (response?.data?.success) {
        setPendingInputs({});
        setUnsavedChanges(false);

        await fetchGridData();

        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Changes saved successfully',
          life: 2000
        });

        if (!filterResult?.isSummaryView) {
          window.dispatchEvent(new CustomEvent('recentChanges:refresh'));
        }
      } else {
        throw new Error('Save failed');
      }
    } catch (err: any) {
      console.error('Save manual input failed', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save changes. Please try again.',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmSave = () => {
    confirmDialog({
      message: 'Are you sure you want to save the changes?',
      header: 'Confirm Save',
      icon: 'pi pi-exclamation-triangle',
      accept: handleSaveChanges,
      reject: () => { },
      acceptLabel: 'Yes',
      rejectLabel: 'No'
    });
  };

  /* ---------------- UI ---------------- */

  return (
    <>
      {loading && <SuspenseLoader />}
      <Toast ref={toast} />

      <div className="excel">

        <GridHeader
          isQuarterly={isQuaterly}
          isSummary={filterResult?.isSummaryView}
          onToggle={setIsQuaterly}
          onSave={confirmSave}
          onExport={handleExportExcel}
          hasChanges={hasChanges}
          onRefresh={handleRefresh}
          filterInfo={{
            year: filterResult?.year,
            customerName: filterResult?.customerName,
            categoryName: filterResult?.categoryName,
            brandName: filterResult?.brandName
          }}
        />

        <div className="ag-theme-alpine grid-wrapper" onContextMenu={(e) => e.preventDefault()}>
          <AgGridReact
            domLayout="normal"
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            onCellValueChanged={onCellValueChanged}
            getRowId={(params) => params.data.rowId}
            suppressRowVirtualisation
            suppressScrollOnNewData
            debounceVerticalScrollbar
            suppressColumnVirtualisation={false}
            animateRows={false}
            suppressAnimationFrame
            stopEditingWhenCellsLoseFocus
            suppressMovableColumns
            suppressDragLeaveHidesColumns
            rowDragManaged={false}
            onCellContextMenu={(params) => {
              params?.event?.preventDefault();

              // disable everything in quarterly mode
              if (isQuaterly) return;

              const field = params.column.getColId();
              const cellId = `${params.data.driverId}-${field}`;
              const isManualField = params.data?.editableMap?.[field] === true;
              const isZeroManual = isManualField && Number(params.value) === 0;
              const hasExistingComment = !!comments[cellId];

              // allow comments only on editable cells
              if (isZeroManual) {
                toast.current?.show({
                  severity: 'warn',
                  summary: 'Not Allowed',
                  detail: 'Cannot add comment for zero value.',
                  life: 2000
                });
                return;
              }

              if (hasExistingComment) {
                return;
              }

              if (
                field === 'title'
                || field === 'Total'
                || params.data.section === 'spacer'
                || !isManualField
                || isZeroManual
              ) {
                return;
              }

              setActiveCell({
                driverId: params.data.driverId,
                col: params.column.getColId()
              });

              setMenuPosition({
                top: (params?.event as MouseEvent)?.clientY,
                left: (params?.event as MouseEvent)?.clientX
              });

              setShowMenu(true);
            }}
          />

        </div>

        {showCommentBox && activeCell && (
          <ExcelCommentBox
            cellId={`${activeCell.driverId}-${activeCell.col}`}
            user={initials}
            comment={comments[`${activeCell.driverId}-${activeCell.col}`]}
            readOnly={!!comments[`${activeCell.driverId}-${activeCell.col}`]}
            position={commentPosition}
            onSave={handleSaveComment}
            onClose={() => setShowCommentBox(false)}
          />
        )}

        {hoveredCell && !showCommentBox && (
          <ExcelCommentBox
            mode="hover"
            cellId={`${hoveredCell.driverId}-${hoveredCell.col}`}
            user={initials}
            comment={comments[`${hoveredCell.driverId}-${hoveredCell.col}`]}
            position={hoverPosition}
            onSave={handleSaveComment}
            onClose={() => setHoveredCell(null)}
            readOnly={false}
            onMouseEnter={() => {
              clearTimeout(hoverTimeoutRef.current);
            }}
            onMouseLeave={() => {
              hoverTimeoutRef.current = setTimeout(() => {
                setHoveredCell(null);
              }, 200);
            }}
          />
        )}

        {showMenu && (
          <div
            className="excel-context-menu"
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: menuPosition.top,
              left: menuPosition.left,
              zIndex: 9999
            }}
          >
            <div
              className="menu-item"
              onClick={() => {
                setShowMenu(false);

                const rect = {
                  top: menuPosition.top,
                  left: menuPosition.left
                };

                setCommentPosition(rect);
                setShowCommentBox(true);
              }}
            >
              Add Comment
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default AGGrid;
